import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  IconButton,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import PeopleIcon from '@mui/icons-material/People';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import LocalCarWashIcon from '@mui/icons-material/LocalCarWash';
import PaymentIcon from '@mui/icons-material/Payment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { getAdminRole, getAdminStoreId, getAdminStoreCode } from '../utils/auth';
import QueryError from '../components/QueryError';
import { getMirrorSummary, getMirrorTaxonomy } from '../api/modernwashMirror';
import MirrorFilterBar from './modernwash/MirrorFilterBar';
import FreshnessBanner from './modernwash/FreshnessBanner';
import useMirrorFilters from './modernwash/useMirrorFilters';
import {
  toBackendPeriod,
  periodLabel,
  scopeLabel,
  categoryLabel,
  renderMetricValue,
} from './modernwash/mirrorFormat';

const providerLabel = {
  local: '이메일',
  custom: '이메일',  // 구버전 호환
  kakao: '카카오',
  apple: '애플',
  google: '구글',
  naver: '네이버',
};

// 가입 경로별 색상 (chip 시각 구분)
const providerColor = {
  local:  { bg: '#F1F5F9', fg: '#475569', border: '#CBD5E1' },  // 회색
  custom: { bg: '#F1F5F9', fg: '#475569', border: '#CBD5E1' },
  kakao:  { bg: '#FEF9C3', fg: '#854D0E', border: '#FDE68A' },  // 카카오 노랑
  google: { bg: '#DBEAFE', fg: '#1E40AF', border: '#BFDBFE' },  // 구글 파랑
  apple:  { bg: '#1F2937', fg: '#FFFFFF', border: '#374151' },  // 애플 검정
  naver:  { bg: '#DCFCE7', fg: '#166534', border: '#BBF7D0' },  // 네이버 초록
};

const productLabel = {
  daily_subscription: '데일리',
  all_in_one_subscription: '올인원',
};

const surfaceSx = {
  // 테마 배수(4 → 48px)가 아니라 surface 규격 12px을 명시한다.
  borderRadius: '12px',
  borderColor: 'divider',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  backgroundColor: '#fff',
};

/**
 * [레거시 동기화 집계 기준 시각]
 *
 * '오늘 매출/결제'와 '오늘 세차'는 `payments`·`wash_histories` 컬렉션에서 나온다.
 * 이 둘은 우리 백엔드가 실시간으로 쓰는 데이터가 아니라 **EC2 cron 의 daily-sync 가
 * 외주 IoT 에서 끌어오는 스트림**이다
 * (docs/legacy-data-separation.md:43 — "③ 레거시/현장 스트림").
 *
 * 그래서 그날 첫 동기화가 반영되기 전에는 오늘 문서가 없어 값이 0 으로 나온다.
 * 화면은 그걸 "오늘 결제 0건 / 매출 0원"으로 보여줬다.
 * **"장사가 안 됐다"와 "아직 집계가 안 됐다"가 같은 화면**이었다.
 *
 * 값을 숨기지는 않는다 — 대신 어느 시점 기준인지 카드에 적어 0 의 의미를 구분한다.
 *
 * ── cron 예정 스케줄 (2026-08-07 EC2 읽기 전용 확인) ──────────────────
 * 서버 timezone 은 **Etc/UTC** 다. crontab 은 UTC 로 해석되므로 KST 로 환산해야 한다.
 *
 *   0 23 * * *  UTC → 08:00 KST
 *   0  3 * * *  UTC → 12:00 KST
 *   0  9 * * *  UTC → 18:00 KST
 *
 * ⚠ 이 값은 **cron 이 도는 예정(시작) 시각**이다. 동기화가 그 시각에 **완료·성공했다는 뜻이 아니다.**
 *   실행이 밀리거나 실패했을 수 있고, **이 화면은 마지막 성공 동기화 시각을 조회하지 않는다**
 *   (그런 API 가 없다). 그래서 문구는 "언제 기준인지" 만 알리고, 표시된 0 이 실제 0 이라고
 *   단정하지 않는다. 실제 데이터 신선도 표시는 별도 백엔드 과제다 —
 *   마지막 **성공** 동기화 시각을 API 로 내려주면 예정 시각 대신 그것을 쓸 수 있다.
 *
 * 문서와의 관계: `docs/INFRA.md` 는 한때 "09 / 18 / 24시 KST" 로 적혀 있었다
 *   (crontab 을 KST 로 오해 — `0 9` 를 09시 KST 로 읽으면 실제는 18시 KST).
 *   **PR #491 에서 문서 5곳을 08·12·18 로 정정했으므로 지금은 문서와 이 파일이 일치한다.**
 *   그래도 판단 기준은 **crontab** 이다. 어긋나면 crontab 을 따르고 문서를 고칠 것.
 */
export const SYNC_HOURS_KST = [8, 12, 18];

/** 브라우저 시간대와 무관하게 KST 시(0~23)를 구한다. */
export function kstHour(now = new Date()) {
  const h = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul', hour: '2-digit', hour12: false,
  }).format(now);
  return Number(h) % 24; // 자정을 '24'로 주는 구현이 있어 정규화한다
}

/**
 * 카드에 붙일 집계 기준 문구.
 * 그날 첫 cron **예정 시각 전**에는 0 을 '없음'으로 읽지 않게 알린다.
 * 시각은 위 SYNC_HOURS_KST(crontab 환산값)에서만 끌어온다 — 문구와 판정이 어긋나지 않게.
 *
 * 이 함수는 **예정 시각만** 안다. 동기화가 실제로 돌았는지·성공했는지는 판단하지 않는다.
 */
export function syncBasisNote(now = new Date()) {
  const h = kstHour(now);
  const [first] = SYNC_HOURS_KST;
  if (h < first) {
    // 오늘 예정된 cron 이 아직 하나도 시작되지 않은 구간이다(직전 예정분은 어제 18시).
    return `오늘 ${String(first).padStart(2, '0')}시 첫 집계 전 — 0으로 보일 수 있어요`;
  }
  const last = SYNC_HOURS_KST.filter((s) => s <= h).pop();
  const schedule = SYNC_HOURS_KST.map((s) => String(s).padStart(2, '0')).join('·');
  return `${last}시 집계 기준 (${schedule}시 갱신)`;
}

const StatCard = ({ icon, label, value, sub, note, scope, color, onClick }) => (
    <Paper
        variant="outlined"
        onClick={onClick}
        sx={{
          ...surfaceSx,
          p: { xs: 1.75, md: 2.25 },
          minHeight: { xs: 96, md: 118 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.5, md: 2 },
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          '&:hover': onClick
              ? {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
              }
              : {},
        }}
    >
      <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color,
            backgroundColor: alpha(color, 0.12),
          }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography
            sx={{
              fontSize: { xs: 24, md: 30 },
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
            }}
            noWrap
        >
          {value}
        </Typography>
        {sub ? (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.75 }}>
              {sub}
            </Typography>
        ) : null}
        {note ? (
            <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }} noWrap>
              {note}
            </Typography>
        ) : null}
        {scope ? (
            <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.5, fontWeight: 600 }} noWrap>
              {scope}
            </Typography>
        ) : null}
      </Box>
    </Paper>
);

const SectionCard = ({ title, subtitle, action, children, minHeight }) => (
    <Paper
        variant="outlined"
        sx={{
          ...surfaceSx,
          p: 3,
          height: '100%',
          minHeight,
        }}
    >
      <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            mb: 2.5,
          }}
      >
        <Box>
          <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 700, letterSpacing: '-0.02em' }}>
            {title}
          </Typography>
          {subtitle ? (
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5, display: { xs: 'none', md: 'block' } }}>
                {subtitle}
              </Typography>
          ) : null}
        </Box>
        {action}
      </Box>
      {children}
    </Paper>
);

/**
 * 월별 매출·구독 캘린더
 * GET /dashboard/calendar?year=&month= → 일별 { revenue, newSubscribers, cancelledSubscribers, netSubscribers }
 * 각 날짜 칸: 매출(₩) + 구독 순증감(신규−해지, +초록/−빨강)
 */
const MonthlyCalendar = () => {
  const now = new Date();
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  /**
   * 조회 실패를 "매출 0원"과 구분하기 위한 상태.
   *
   * 이전에는 catch 가 setDays([]) 만 했다. days 가 비면 total.revenue 가 0 이 되고
   * 부제가 `won(0) || '+0'` 으로 떨어져 **"이번 달 매출 +0"** 이 떴다.
   * 매출이 실제로 0원인 달과 서버에 못 물어본 달이 같은 화면이 되는 것이라,
   * 정산 대사에서 판단이 반대로 뒤집힌다.
   */
  const [loadError, setLoadError] = useState(null);
  // 날짜 클릭 상세
  const [detailDate, setDetailDate] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDay = (dateKey) => {
    setDetailDate(dateKey);
    setDetail(null);
    setDetailLoading(true);
    api
      .get(`/dashboard/calendar-day?date=${dateKey}`)
      .then((res) => setDetail(res.data))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  };

  // 재시도 버튼이 같은 요청을 다시 태울 수 있도록 effect 밖으로 뺀다.
  const fetchCalendar = useCallback(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/dashboard/calendar?year=${ym.year}&month=${ym.month}`)
      .then((res) => { if (alive) { setDays(res.data?.days || []); setLoadError(null); } })
      .catch((err) => { if (alive) { setDays([]); setLoadError(err); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ym.year, ym.month]);

  useEffect(() => fetchCalendar(), [fetchCalendar]);

  const byDate = useMemo(() => {
    const m = {};
    days.forEach((d) => { m[d.date] = d; });
    return m;
  }, [days]);

  const total = useMemo(
    () => days.reduce(
      (a, d) => ({ revenue: a.revenue + d.revenue, net: a.net + d.netSubscribers }),
      { revenue: 0, net: 0 },
    ),
    [days],
  );

  const goPrev = () => setYm((p) => (p.month === 1 ? { year: p.year - 1, month: 12 } : { ...p, month: p.month - 1 }));
  const goNext = () => setYm((p) => (p.month === 12 ? { year: p.year + 1, month: 1 } : { ...p, month: p.month + 1 }));

  const pad = (n) => String(n).padStart(2, '0');
  const firstDow = new Date(ym.year, ym.month - 1, 1).getDay(); // 0=일
  const daysInMonth = new Date(ym.year, ym.month, 0).getDate();
  const todayKey = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  // 금액 표기: 한글 단위(만/원) 없이 부호 + 전체 숫자 (예: +102,300)
  const won = (n) => (n ? `+${n.toLocaleString()}` : '');
  const netColor = (n) => (n > 0 ? '#16A34A' : n < 0 ? '#DC2626' : 'text.disabled');
  const dows = ['일', '월', '화', '수', '목', '금', '토'];

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const action = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        overflow: 'hidden',
        height: 40,
      }}
    >
      <IconButton size="small" onClick={goPrev} sx={{ borderRadius: 0, height: '100%', px: 1 }}>
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <Typography sx={{ fontWeight: 700, fontSize: 14, minWidth: 104, textAlign: 'center', px: 1 }}>
        {ym.year}년 {ym.month}월
      </Typography>
      <IconButton size="small" onClick={goNext} sx={{ borderRadius: 0, height: '100%', px: 1 }}>
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <>
    <SectionCard
      title="매출·구독 캘린더"
      subtitle={
        loadError
          ? '조회에 실패해 이번 달 집계를 표시할 수 없습니다'
          : `이번 달 매출 ${won(total.revenue) || '+0'} · 구독 순증감 ${total.net >= 0 ? '+' : ''}${total.net}`
      }
      action={action}
    >
      {/* 렌더 분기: 로딩 → 오류 → (그 외) 달력.
          오류일 때 달력 격자를 함께 그리면 전부 빈 칸이라 "매출 0원인 달"과 구분되지 않는다. */}
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {loadError ? (
        <QueryError error={loadError} onRetry={fetchCalendar} context="매출·구독 캘린더" />
      ) : (
      <>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: { xs: 0.5, md: 1 }, mb: 1 }}>
        {dows.map((d, i) => (
          <Typography key={d} sx={{ minWidth: 0, textAlign: 'center', fontSize: { xs: 11, md: 12 }, fontWeight: 700, color: i === 0 ? '#DC2626' : i === 6 ? '#2563EB' : 'text.secondary' }}>
            {d}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: { xs: 0.5, md: 1.25 } }}>
        {cells.map((d, idx) => {
          if (d === null) return <Box key={`b${idx}`} />;
          const k = `${ym.year}-${pad(ym.month)}-${pad(d)}`;
          const info = byDate[k];
          const isToday = k === todayKey;
          const dow = (firstDow + d - 1) % 7;
          const net = info?.netSubscribers || 0;
          return (
            <Box
              key={k}
              onClick={() => openDay(k)}
              sx={{
                border: '1px solid',
                borderColor: isToday ? 'primary.main' : 'divider',
                borderRadius: '8px',
                minWidth: 0,
                px: { xs: 0.5, md: 2 },
                py: { xs: 1, md: 1.75 },
                minHeight: { xs: 66, md: 116 },
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 0.5, md: 1 },
                cursor: 'pointer',
                transition: 'background-color .12s, box-shadow .12s',
                backgroundColor: isToday ? alpha('#2563EB', 0.05) : 'background.paper',
                '&:hover': { backgroundColor: alpha('#2563EB', 0.08), boxShadow: 1 },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 11, md: 13 },
                  fontWeight: 700,
                  lineHeight: 1,
                  color: dow === 0 ? '#DC2626' : dow === 6 ? '#2563EB' : 'text.secondary',
                }}
              >
                {d}
              </Typography>
              <Stack spacing={0.4} sx={{ minWidth: 0, overflow: 'hidden' }}>
                {info?.revenue ? (
                  <Typography sx={{ fontSize: { xs: 9, md: 13 }, fontWeight: 800, color: 'text.primary' }} noWrap>
                    {won(info.revenue)}
                  </Typography>
                ) : null}
                {net !== 0 ? (
                  <Typography sx={{ fontSize: { xs: 8.5, md: 11.5 }, fontWeight: 700, color: netColor(net) }} noWrap>
                    구독 {net > 0 ? `+${net}` : net}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          );
        })}
      </Box>
      </>
      )}
    </SectionCard>

    {/* 날짜 클릭 → 결제 내역 상세 (구독/현장/환불 분리 + 리스트) */}
    <Dialog open={!!detailDate} onClose={() => setDetailDate(null)} fullWidth maxWidth="sm">
      <DialogTitle>{detailDate} 결제 내역</DialogTitle>
      <DialogContent dividers>
        {detailLoading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
        ) : !detail || detail.summary.count === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>이 날짜의 결제 내역이 없습니다.</Typography>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 2 }}>
              {[
                { label: '구독', value: detail.summary.subscriptionRevenue, color: '#2563EB' },
                { label: '현장(1일권)', value: detail.summary.onsiteRevenue, color: '#7C3AED' },
                { label: '환불', value: detail.summary.refund, color: '#DC2626' },
                { label: '합계', value: detail.summary.total, color: '#16A34A' },
              ].map((s) => (
                <Box key={s.label} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', p: 1.25 }}>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  <Typography sx={{ fontWeight: 800, color: s.color }}>₩{(s.value || 0).toLocaleString()}</Typography>
                </Box>
              ))}
            </Box>
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>시간</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>구분</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>결제수단</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>차량</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">금액</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.payments.map((p, i) => (
                    <TableRow key={i} hover>
                      <TableCell>{new Date(p.time).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" label={p.type === 'subscription' ? '구독' : '현장'} />
                      </TableCell>
                      <TableCell>{p.method || '-'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{p.plate || '-'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: p.amount < 0 ? '#DC2626' : 'text.primary' }}>
                        {p.amount.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {detail.summary.count >= 300 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                최근 300건만 표시됩니다.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

const InlineMetric = ({ label, value, icon, color }) => (
    <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          p: { xs: 1.5, md: 2 },
          minHeight: { xs: 72, md: 88 },
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          backgroundColor: '#FCFCFD',
        }}
    >
      <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
            backgroundColor: alpha(color, 0.12),
            flexShrink: 0,
          }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700 }} noWrap>
          {value}
        </Typography>
      </Box>
    </Box>
);

const EmptyState = ({ text }) => (
    <Box
        sx={{
          py: 6,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
    >
      <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{text}</Typography>
    </Box>
);

// ─────────────────────────────────────────
// WMO 날씨 코드 → 이모지 + 한글 설명
// Open-Meteo API 기준 (무료, API Key 불필요)
// ─────────────────────────────────────────
function wmoToIcon(code) {
  if (code === 0)               return { emoji: '☀️',  label: '맑음' };
  if (code <= 2)                return { emoji: '🌤️', label: '구름 조금' };
  if (code === 3)               return { emoji: '☁️',  label: '흐림' };
  if (code <= 48)               return { emoji: '🌫️', label: '안개' };
  if (code <= 55)               return { emoji: '🌦️', label: '이슬비' };
  if (code <= 65)               return { emoji: '🌧️', label: '비' };
  if (code <= 77)               return { emoji: '❄️',  label: '눈' };
  if (code <= 82)               return { emoji: '🌧️', label: '소나기' };
  if (code <= 99)               return { emoji: '⛈️',  label: '뇌우' };
  return { emoji: '🌡️', label: '알 수 없음' };
}

const DAY_KO = ['일', '월', '화', '수', '목', '금', '토'];

// 기본 위치 (서울) — 지점 좌표 없을 때 폴백
const DEFAULT_LAT = 37.5665;
const DEFAULT_LNG = 126.9780;

function WeatherWidget({ lat = DEFAULT_LAT, lng = DEFAULT_LNG }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setWeather(null);
    setLoading(true);
    fetch(
      `https://api.open-meteo.com/v1/forecast` +
      `?latitude=${lat}&longitude=${lng}` +
      `&daily=weathercode,temperature_2m_max,temperature_2m_min` +
      `&timezone=Asia%2FSeoul&forecast_days=7`
    )
      .then((r) => r.json())
      .then((d) => setWeather(d.daily))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [lat, lng]);

  if (loading) return (
    <Box sx={{ py: 2, textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary">날씨 불러오는 중...</Typography>
    </Box>
  );
  if (!weather) return (
    <Box sx={{ py: 2, textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary">날씨 정보 없음</Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: 0.75, sm: 1, md: 1.5 },
        // 카드별 최소폭(아래) × 7개를 viewport가 못 받으면 가로 스크롤 폴백
        overflowX: 'auto',
        pb: 0.5,
        '&::-webkit-scrollbar': { height: 6 },
        '&::-webkit-scrollbar-thumb': { backgroundColor: '#CBD5E1', borderRadius: 3 },
        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
      }}
    >
      {weather.time.map((dateStr, i) => {
        const date    = new Date(dateStr);
        const dayKo   = DAY_KO[date.getDay()];
        const isToday = i === 0;
        const isWeekend = date.getDay() === 0 || date.getDay() === 6;  // 일/토
        const { emoji, label } = wmoToIcon(weather.weathercode[i]);
        const tMax = Math.round(weather.temperature_2m_max[i]);
        const tMin = Math.round(weather.temperature_2m_min[i]);

        return (
          <Box
            key={dateStr}
            sx={{
              flex: '1 1 0',
              // 카드별 최소폭 보장 — 카드 안 텍스트 안 깨짐
              // 넓은 viewport: 7개가 균등 분할되며 화면 가득
              // 좁은 viewport: minWidth 합산이 viewport 초과 → 가로 스크롤
              minWidth: 90,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              p: 2,
              borderRadius: { xs: 2.5, md: 3 },
              background: isToday
                ? 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)'
                : '#FAFBFD',                                   // 평일도 카드처럼
              border: '1px solid',
              borderColor: isToday ? '#93C5FD' : 'rgba(0,0,0,0.04)',
              boxShadow: isToday ? '0 4px 12px rgba(37,99,235,0.12)' : 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: isToday
                  ? '0 6px 16px rgba(37,99,235,0.18)'
                  : '0 4px 12px rgba(0,0,0,0.06)',
                borderColor: isToday ? '#93C5FD' : 'rgba(0,0,0,0.08)',
              },
            }}
          >
            {/* 날짜 — 줄바꿈 방지 */}
            <Typography sx={{
              fontSize: 12,
              fontWeight: isToday ? 800 : 600,
              color: isToday
                ? '#1D4ED8'
                : isWeekend ? '#DC2626' : 'text.secondary',
              letterSpacing: '-0.01em',
              whiteSpace: 'nowrap',
            }}>
              {isToday ? '오늘' : `${date.getMonth() + 1}/${date.getDate()}(${dayKo})`}
            </Typography>

            {/* 이모지 */}
            <Typography sx={{ fontSize: 36, lineHeight: 1, my: 0.5 }}>{emoji}</Typography>

            {/* 날씨 텍스트 */}
            <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</Typography>

            {/* 온도 — 가로 배치, 줄바꿈 방지 */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.5, whiteSpace: 'nowrap' }}>
              <Typography component="span" sx={{ fontSize: 16, fontWeight: 800, color: '#DC2626', lineHeight: 1 }}>
                {tMax}°
              </Typography>
              <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, color: '#3B82F6', lineHeight: 1 }}>
                {tMin}°
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ─── 시간대별 이용 히트맵 ──────────────────────────────────────
const DOW_KO = ['월', '화', '수', '목', '금', '토', '일'];
const HOURS  = Array.from({ length: 24 }, (_, i) => i);

// 운영 시간대 (이 범위 밖은 비운영으로 표시)
const OP_START = 7;
const OP_END   = 21;

function UsageHeatmap({ data }) {
  const lookup = {};
  for (const d of data) {
    if (!lookup[d.dow]) lookup[d.dow] = {};
    lookup[d.dow][d.hour] = d.count;
  }

  // 운영 시간대 데이터만으로 색상 스케일 계산 (비운영 시간 이상치에 영향받지 않도록)
  const opData    = data.filter((d) => d.hour >= OP_START && d.hour <= OP_END);
  const maxCount  = Math.max(...opData.map((d) => d.count), 1);

  const getColor = (count, isOp) => {
    if (!isOp) return null; // 비운영 시간: 별도 스타일
    if (!count) return '#F1F5F9';
    const ratio = count / maxCount;
    return `rgba(37, 99, 235, ${(0.08 + 0.85 * ratio).toFixed(2)})`;
  };

  // ── 인사이트 계산 (운영 시간 기준) ─────────────────────────
  const dowTotals = DOW_KO.map((_, dow) =>
    HOURS.filter((h) => h >= OP_START && h <= OP_END)
      .reduce((sum, h) => sum + (lookup[dow]?.[h] || 0), 0)
  );
  const peakDowIdx  = dowTotals.indexOf(Math.max(...dowTotals));
  const quietDowIdx = dowTotals.indexOf(Math.min(...dowTotals.filter((v) => v > 0)));

  const hourTotals = HOURS.map((h) =>
    DOW_KO.reduce((sum, _, dow) => sum + (lookup[dow]?.[h] || 0), 0)
  );
  const opHourTotals = hourTotals.filter((_, h) => h >= OP_START && h <= OP_END);
  const peakHour  = OP_START + opHourTotals.indexOf(Math.max(...opHourTotals));

  const slotSum = (from, to) =>
    HOURS.filter((h) => h >= from && h <= to).reduce((s, h) => s + hourTotals[h], 0);
  const slots = [
    { label: '오전 (7~11시)',  count: slotSum(7, 11) },
    { label: '오후 (12~16시)', count: slotSum(12, 16) },
    { label: '저녁 (17~21시)', count: slotSum(17, 21) },
  ];
  const peakSlot = slots.reduce((a, b) => (a.count > b.count ? a : b));
  const total    = opData.reduce((s, d) => s + d.count, 0);

  // 주말 vs 평일 비율
  const weekdayTotal = [0,1,2,3,4].reduce((s, dow) => s + dowTotals[dow], 0);
  const weekendTotal = [5,6].reduce((s, dow) => s + dowTotals[dow], 0);
  const weekdayPct   = total > 0 ? Math.round(weekdayTotal / (weekdayTotal + weekendTotal) * 100) : 0;

  // 가장 바쁜 요일+시간 슬롯
  let peakSlotCount = 0;
  for (let dow = 0; dow < 7; dow++) {
    for (let h = OP_START; h <= OP_END; h++) {
      const c = lookup[dow]?.[h] || 0;
      if (c > peakSlotCount) { peakSlotCount = c; }
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        // lg(1200px+)에서만 가로 배치 — 그 이하는 인사이트가 짜부됨
        flexDirection: { xs: 'column', lg: 'row' },
        gap: { xs: 2, lg: 4 },
        alignItems: 'stretch',
      }}
    >

      {/* ── 히트맵 격자 (모바일은 가로 스크롤) ── */}
      <Box
        sx={{
          flexShrink: 0,
          overflowX: { xs: 'auto', md: 'visible' },
          maxWidth: '100%',
          pb: { xs: 1, md: 0 },
        }}
      >
        {/* 시간 라벨 */}
        <Box sx={{ display: 'flex', ml: '32px', mb: 0.5 }}>
          {HOURS.map((h) => (
            <Box key={h} sx={{
              width: 22, textAlign: 'center', fontSize: 9.5,
              color: h < OP_START || h > OP_END ? '#D1D5DB' : '#94A3B8',
              flexShrink: 0,
            }}>
              {h % 3 === 0 ? `${h}시` : ''}
            </Box>
          ))}
        </Box>

        {/* 격자 */}
        {DOW_KO.map((day, dow) => (
          <Box key={dow} sx={{ display: 'flex', alignItems: 'center', mb: 0.4 }}>
            <Typography sx={{ width: 32, fontSize: 11, fontWeight: 600, color: '#64748B', flexShrink: 0 }}>
              {day}
            </Typography>
            {HOURS.map((h) => {
              const count = lookup[dow]?.[h] || 0;
              const isOp  = h >= OP_START && h <= OP_END;
              const bg    = getColor(count, isOp);
              return (
                <Tooltip
                  key={h}
                  title={isOp
                    ? `${day}요일 ${h}시 — ${count.toLocaleString()}건`
                    : `${day}요일 ${h}시 — 비운영 (${count}건)`}
                  arrow
                >
                  <Box sx={{
                    width: 22, height: 22, mr: 0.3,
                    borderRadius: 0.8,
                    flexShrink: 0,
                    cursor: 'default',
                    transition: 'transform 0.12s',
                    '&:hover': { transform: 'scale(1.5)', zIndex: 2, position: 'relative' },
                    ...(isOp
                      ? { backgroundColor: bg }
                      : {
                          backgroundColor: '#F8FAFC',
                          backgroundImage: 'repeating-linear-gradient(-45deg, transparent, transparent 3px, #E2E8F0 3px, #E2E8F0 4px)',
                          border: count > 0 ? '1px solid #FDA4AF' : 'none',
                        }
                    ),
                  }} />
                </Tooltip>
              );
            })}
          </Box>
        ))}

        {/* 범례 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, ml: '32px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: 10, color: '#94A3B8' }}>적음</Typography>
            {[0.08, 0.3, 0.55, 0.75, 0.93].map((a, i) => (
              <Box key={i} sx={{ width: 14, height: 14, borderRadius: 0.5, backgroundColor: `rgba(37,99,235,${a})` }} />
            ))}
            <Typography sx={{ fontSize: 10, color: '#94A3B8' }}>많음</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{
              width: 14, height: 14, borderRadius: 0.5,
              backgroundColor: '#F8FAFC',
              backgroundImage: 'repeating-linear-gradient(-45deg, transparent, transparent 3px, #E2E8F0 3px, #E2E8F0 4px)',
            }} />
            <Typography sx={{ fontSize: 10, color: '#94A3B8' }}>비운영</Typography>
          </Box>
        </Box>
      </Box>

      {/* ── 인사이트 패널 ── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* lg+: 히트맵 우측에 2x3, 이하: 히트맵 아래 가로 그리드
            xs(작음): 2열, sm+: 3열, lg+: 2열(우측 세로 배치) */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(2, 1fr)' },
            gridTemplateRows: { xs: 'auto', lg: 'repeat(3, 1fr)' },
            gap: 1,
            height: '100%',
          }}
        >
          {[
            { emoji: '🏆', label: '가장 바쁜 요일',   value: `${DOW_KO[peakDowIdx]}요일`,           sub: `${dowTotals[peakDowIdx].toLocaleString()}건` },
            { emoji: '😴', label: '가장 한산한 요일', value: `${DOW_KO[quietDowIdx]}요일`,          sub: `${dowTotals[quietDowIdx].toLocaleString()}건` },
            { emoji: '⏰', label: '피크 시간대',       value: `${peakHour}시`,                       sub: `${hourTotals[peakHour].toLocaleString()}건` },
            { emoji: '📅', label: '평일 / 주말',       value: `${weekdayPct}% / ${100-weekdayPct}%`, sub: `평일 ${weekdayTotal.toLocaleString()}건` },
            { emoji: '🔥', label: '최다 이용 구간',   value: peakSlot.label,                        sub: `${peakSlot.count.toLocaleString()}건` },
            { emoji: '📆', label: '하루 평균 이용',   value: `${Math.round(total / 180)}건`,         sub: '6개월 평균' },
          ].map(({ emoji, label, value, sub }) => (
            <Box key={label} sx={{
              px: 1.5, py: 1, borderRadius: '8px',
              backgroundColor: '#F8FAFC',
              border: '1px solid #E2E8F0',
              display: 'flex', gap: 1, alignItems: 'center',
            }}>
              <Typography sx={{ fontSize: 15, lineHeight: 1, flexShrink: 0 }}>{emoji}</Typography>
              <Box>
                <Typography sx={{ fontSize: 9.5, color: '#94A3B8', lineHeight: 1, mb: 0.5 }}>{label}</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B', lineHeight: 1, mb: 0.4 }}>{value}</Typography>
                <Typography sx={{ fontSize: 10.5, color: '#64748B', lineHeight: 1 }}>{sub}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

    </Box>
  );
}
// ──────────────────────────────────────────────────────────────────

const DashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heatmap, setHeatmap] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  // 날씨용 지점 정보 — JWT 에서 (localStorage 변조 방지)
  const role    = getAdminRole();    // 'master' | 'manager' | null
  const storeId = getAdminStoreId(); // manager 의 지점 ID
  const storeCode = getAdminStoreCode();
  const isMaster = role === 'master';
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(null); // 날씨 위젯 지점 선택

  // ── 서비스/카테고리/기간/매장 필터 (URL 유지). 기본=Mogoo 전체(네이티브) ──
  const [filters, updateFilters] = useMirrorFilters({ defaultService: 'mogoo' });
  const isModernwash = filters.service === 'modernwash';
  const periodState = useMemo(
    () => ({ period: filters.period, month: filters.month, start: filters.start, end: filters.end }),
    [filters.period, filters.month, filters.start, filters.end],
  );
  const storeParam = isMaster ? filters.storeId || undefined : undefined; // manager 는 storeId 미전송

  // 카테고리 목록의 단일 소스 — 백엔드 taxonomy(두 서비스 모두). 프론트 하드코딩 금지.
  const [taxonomy, setTaxonomy] = useState(null);
  useEffect(() => {
    let alive = true;
    getMirrorTaxonomy()
      .then((t) => alive && setTaxonomy(t))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // 매장 목록 — 두 모드 공통(필터 바 매장 셀렉터 + 날씨). KPI 소스가 아니라 스코프 조회용.
  useEffect(() => {
    let alive = true;
    api.get('/stores').then((res) => {
      if (!alive) return;
      const list = res.data || [];
      setStores(list);
      if (role === 'manager' && storeId) {
        setSelectedStoreId(storeId);
      } else if (list.length > 0) {
        const preferred = list.find((s) => s.lat && s.lng) ?? list[0];
        setSelectedStoreId(preferred._id);
      }
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 네이티브(모구) 지표 — service='mogoo' 에서만 호출. 미러와 절대 섞지 않는다. ──
  useEffect(() => {
    if (isModernwash) {
      setLoading(false); // 미러 모드에선 네이티브 초기 로딩 게이트를 풀어준다.
      return undefined;
    }
    fetchStats();
    api.get('/usage/heatmap').then((res) => setHeatmap(res.data || [])).catch(() => {});
    const timer = setInterval(() => fetchStats(true), 30_000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModernwash]);

  // ── 미러(ModernWash) 요약 — service='modernwash' 에서만 호출. 네이티브 stats 는 부르지 않는다. ──
  const [mwSummary, setMwSummary] = useState(null);
  const [mwLoading, setMwLoading] = useState(false);
  const [mwError, setMwError] = useState(null);
  const backendPeriod = useMemo(() => toBackendPeriod(periodState), [periodState]);
  const backendPeriodKey = `${backendPeriod.period}|${backendPeriod.start || ''}|${backendPeriod.end || ''}`;

  const loadMirror = useCallback(() => {
    let alive = true;
    setMwLoading(true);
    setMwError(null);
    const base = { service: 'modernwash', storeId: storeParam, ...backendPeriod };
    // summary 의 freshness 는 KPI 와 같은 active snapshot 에서 계산된다.
    // 별도 freshness 요청을 병렬로 보내면 동기화 포인터가 그 사이 승격될 때
    // 배너와 숫자가 서로 다른 실행을 가리킬 수 있으므로, 이 화면은 summary 하나만 기준으로 삼는다.
    getMirrorSummary(base).then((summary) => {
      if (!alive) return;
      setMwSummary(summary);
    }).catch((error) => {
      if (alive) setMwError(error);
    }).finally(() => { if (alive) setMwLoading(false); });
    return () => { alive = false; };
    // washPeriod/backendPeriod 는 매 렌더 새 객체 → 원시 키로만 의존한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendPeriodKey, storeParam]);

  useEffect(() => {
    if (!isModernwash) return undefined;
    return loadMirror();
  }, [isModernwash, loadMirror]);

  const fetchStats = async (silent = false) => {
    try {
      const res = await api.get('/dashboard/stats');
      setStats(res.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('대시보드 조회 실패:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const formatDate = (dateStr) =>
      dateStr ? new Date(dateStr).toLocaleDateString('ko-KR') : '';

  const formatCurrency = (amount) =>
      `${(amount || 0).toLocaleString('ko-KR')}원`;

  const conversionRate = stats?.totalMembers
      ? ((stats.activeSubscriptions / stats.totalMembers) * 100).toFixed(1)
      : 0;

  const maxRevenue = useMemo(() => {
    const values = stats?.dailyRevenue?.map((d) => d.total) ?? [];
    return values.length ? Math.max(...values) : 0;
  }, [stats]);

  // 스코프 라벨 (service · category · period)
  const nativeScope = scopeLabel({
    service: 'mogoo',
    categoryText: categoryLabel(taxonomy, 'mogoo', filters.category),
    periodState,
  });
  const mirrorScope = scopeLabel({
    service: 'modernwash',
    categoryText: categoryLabel(taxonomy, 'modernwash', filters.category),
    periodState,
  });

  // 미러 요약 파생값 (never_synced/stale 등에서 절대 0 으로 렌더하지 않는다)
  const mwData = mwSummary?.data || null;
  const mwScopeInfo = mwSummary?.scope || null;
  const mwReason = mwSummary?.reason || null;
  const mwFreshnessEnv = mwSummary?.freshness || null;
  const mwSections = mwData?.sections || {};
  const managerStoreName = mwScopeInfo?.storeName || (storeCode ? `내 매장(${storeCode})` : '내 매장');

  const goModernwash = () => navigate(`/modernwash?${new URLSearchParams({
    ...(filters.category ? { category: filters.category } : {}),
    period: filters.period,
    ...(filters.month ? { month: filters.month } : {}),
    ...(filters.start ? { start: filters.start } : {}),
    ...(filters.end ? { end: filters.end } : {}),
    ...(filters.storeId ? { storeId: filters.storeId } : {}),
  }).toString()}`);

  if (loading && !isModernwash) {
    return (
        <Box sx={{ py: 5 }}>
          <LinearProgress />
          <Typography align="center" sx={{ mt: 2, color: 'text.secondary' }}>
            대시보드 로딩 중...
          </Typography>
        </Box>
    );
  }

  return (
      <Box
          sx={{
            maxWidth: 1440,
            mx: 'auto',
            px: { xs: 2, sm: 3, lg: 4 },
            py: { xs: 2, md: 3 },
          }}
      >
        {/* Header */}
        <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              mb: 3,
            }}
        >
          <Box>
            {/* 모바일에선 AppBar에 이미 "대시보드"가 표시되므로 헤더 자체 숨김 */}
            <Typography
                sx={{
                  fontSize: { xs: 22, md: 34 },
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  display: { xs: 'none', md: 'block' },
                }}
            >
              대시보드
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 0.5, display: { xs: 'none', md: 'block' } }}>
              Mogoo 관리자 현황
            </Typography>
          </Box>

          <Chip
              label={new Date().toLocaleDateString('ko-KR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                weekday: 'long',
              })}
              variant="outlined"
              sx={{ height: 38, borderRadius: 999 }}
          />
          {!isModernwash && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {lastUpdated && (
                <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                  {lastUpdated.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })} 기준
                </Typography>
              )}
              <IconButton size="small" onClick={() => fetchStats()} title="새로고침">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* 서비스/카테고리/기간/매장 필터 (URL 유지). 서비스로 KPI 소스를 전환한다. */}
        <MirrorFilterBar
          value={filters}
          onChange={updateFilters}
          taxonomy={taxonomy}
          stores={stores}
          role={role}
          managerStoreName={managerStoreName}
          showService
          rightSlot={
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewIcon />}
              onClick={goModernwash}
              sx={{ whiteSpace: 'nowrap' }}
            >
              ModernWash 운영 상세
            </Button>
          }
        />

        {/* 미러 모드: 네이티브 지표는 기간/카테고리 필터가 적용되지 않는 고정 개요임을 알린다. */}
        {!isModernwash && (
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: -1.5, mb: 2 }}>
            ※ Mogoo(네이티브) 지표는 고정 개요입니다. 기간·카테고리·매장 필터는 “ModernWash 운영” 스코프에 적용됩니다.
          </Typography>
        )}

        {/* 미러 모드 신선도 배너 */}
        {isModernwash && (
          <Box sx={{ mb: 3 }}>
            <FreshnessBanner freshness={mwFreshnessEnv} reason={mwReason} basisLabel={periodLabel(periodState)} />
          </Box>
        )}
        {isModernwash && mwError && (
          <QueryError error={mwError} onRetry={loadMirror} context="ModernWash 운영 요약" />
        )}
        {isModernwash && mwLoading && <LinearProgress sx={{ mb: 2 }} />}

        {/* KPI Row */}
        <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 2,
              mb: 3,
            }}
        >
          {isModernwash ? (
            <>
              {/* 미러 KPI — 절대 합산/0단정 금지(renderMetricValue). 각 카드에 스코프 라벨. */}
              {(() => {
                const subs = renderMetricValue(mwData?.subscriptionCount, mwSections.subscriptions || 'never_synced', '건');
                const wash = renderMetricValue(mwData?.actualWashCount, mwSections.washRecords || 'never_synced', '건');
                const mem = renderMetricValue(mwData?.memberCount, mwSections.members || 'never_synced', '명');
                const pay = renderMetricValue(mwData?.paymentReference?.grossAmount, mwSections.paymentDailySummaries || 'never_synced', '원');
                return (
                  <>
                    <StatCard icon={<SubscriptionsIcon />} label="활성 구독" value={subs.text} sub="원천 준비중(P2)" note={subs.badge?.note} scope={mirrorScope} color="#16A34A" onClick={goModernwash} />
                    <StatCard icon={<LocalCarWashIcon />} label="실제 세차 (actual_wash)" value={wash.text} sub="인식 오류·중복은 별도 카운트" note={wash.badge?.note} scope={mirrorScope} color="#7C3AED" onClick={goModernwash} />
                    <StatCard icon={<PeopleIcon />} label="회원 수" value={mem.text} note={mem.badge?.note} scope={mirrorScope} color="#2563EB" onClick={goModernwash} />
                    <StatCard icon={<PaymentIcon />} label="운영 매출 참고치" value={pay.text} sub="재무 원장 아님" note={pay.badge?.note} scope={mirrorScope} color="#EA580C" onClick={goModernwash} />
                  </>
                );
              })()}
            </>
          ) : (
            <>
              <StatCard
                  icon={<PeopleIcon />}
                  label="전체 회원"
                  value={(stats?.totalMembers || 0).toLocaleString()}
                  sub={`구독 전환율 ${conversionRate}%`}
                  scope={nativeScope}
                  color="#2563EB"
                  onClick={() => navigate('/members')}
              />
              <StatCard
                  icon={<SubscriptionsIcon />}
                  label="활성 구독"
                  value={(stats?.activeSubscriptions || 0).toLocaleString()}
                  sub={`누적 구독자 ${(stats?.totalCumulativeSubscribers || 0).toLocaleString()}명`}
                  scope={nativeScope}
                  color="#16A34A"
                  onClick={() => navigate('/subscriptions')}
              />
              <StatCard
                  icon={<LocalCarWashIcon />}
                  label="오늘 세차"
                  value={(stats?.todayWashes || 0).toLocaleString()}
                  sub={`주간 ${(stats?.weekWashes || 0).toLocaleString()}건`}
                  note={syncBasisNote()}
                  scope={nativeScope}
                  color="#7C3AED"
              />
              <StatCard
                  icon={<PaymentIcon />}
                  label="오늘 매출"
                  value={formatCurrency(stats?.todayRevenue)}
                  sub={`${stats?.todayPayments || 0}건 결제`}
                  note={syncBasisNote()}
                  scope={nativeScope}
                  color="#EA580C"
              />
            </>
          )}
        </Box>

        {/* 미러 모드: 상세는 /modernwash 전용 화면. 대시보드에는 요약 + 진입 CTA 만 둔다. */}
        {isModernwash && (
          <Paper
            variant="outlined"
            sx={{ borderRadius: '12px', borderColor: 'divider', p: { xs: 2, md: 3 }, mb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, backgroundColor: '#fff' }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: { xs: 16, md: 18 }, fontWeight: 800 }}>ModernWash 운영 상세</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>
                실제 세차 분류 · 인식 이벤트 · 운영 매출 요약 · 세차 기록(일자 드릴다운)을 전용 화면에서 확인하세요. (읽기 전용 미러)
              </Typography>
            </Box>
            <Button variant="contained" endIcon={<OpenInNewIcon />} onClick={goModernwash} sx={{ ml: { md: 'auto' }, whiteSpace: 'nowrap' }}>
              운영 상세 열기
            </Button>
          </Paper>
        )}

        {/* 네이티브(모구) 하위 섹션 — 미러 모드에선 숨겨 데이터 혼합을 원천 차단한다. */}
        {!isModernwash && (
        <>
        {/* ModernWash 운영 진입 카드 (대시보드 상시 노출) */}
        <Paper
          variant="outlined"
          sx={{ borderRadius: '12px', borderColor: 'divider', p: { xs: 2, md: 2.5 }, mb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, backgroundColor: '#fff' }}
        >
          <Box sx={{ width: 44, height: 44, borderRadius: '12px', display: 'grid', placeItems: 'center', flexShrink: 0, color: '#0EA5E9', backgroundColor: alpha('#0EA5E9', 0.12) }}>
            <LocalCarWashIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800 }}>ModernWash 운영 (V1 미러)</Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>세차 레거시 운영 지표를 네이티브와 분리해 봅니다.</Typography>
          </Box>
          <Button variant="outlined" endIcon={<OpenInNewIcon />} onClick={goModernwash} sx={{ ml: { md: 'auto' }, whiteSpace: 'nowrap' }}>
            바로가기
          </Button>
        </Paper>

        {/* 매출·구독 캘린더 (월별) */}
        <Box sx={{ mb: 3 }}>
          <MonthlyCalendar />
        </Box>

        {/* Main Area */}
        <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'minmax(0, 1.7fr) minmax(340px, 0.95fr)',
              },
              gap: 3,
              mb: 3,
            }}
        >
          {/* Left: Main Revenue Overview */}
          <SectionCard
              title="매출 개요"
              subtitle="최근 7일 결제 흐름과 핵심 매출 지표를 한 번에 확인합니다."
              minHeight={540}
              action={
                <Chip
                    icon={<TrendingUpIcon />}
                    label="최근 7일"
                    variant="outlined"
                    sx={{ borderRadius: 999 }}
                />
              }
          >
            <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(3, minmax(0, 1fr))',
                  },
                  gap: 1.5,
                  mb: 2.5,
                }}
            >
              <InlineMetric
                  label="오늘 매출"
                  value={formatCurrency(stats?.todayRevenue)}
                  icon={<CalendarTodayIcon fontSize="small" />}
                  color="#2563EB"
              />
              <InlineMetric
                  label="주간 매출 (7일)"
                  value={formatCurrency(stats?.weekRevenue)}
                  icon={<DateRangeIcon fontSize="small" />}
                  color="#16A34A"
              />
              <InlineMetric
                  label="월간 매출 (30일)"
                  value={formatCurrency(stats?.monthRevenue)}
                  icon={<AccountBalanceWalletIcon fontSize="small" />}
                  color="#7C3AED"
              />
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      날짜
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}
                    >
                      건수
                    </TableCell>
                    <TableCell
                        align="right"
                        sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}
                    >
                      매출
                    </TableCell>
                    {/* 막대 그래프 컬럼 — 모바일에선 숨김 */}
                    <TableCell sx={{ width: '36%', display: { xs: 'none', sm: 'table-cell' } }} />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {stats?.dailyRevenue?.length ? (
                      stats.dailyRevenue.map((day) => {
                        const pct = maxRevenue > 0 ? (day.total / maxRevenue) * 100 : 0;

                        return (
                            <TableRow
                                key={day._id}
                                hover
                                sx={{
                                  '& td': {
                                    borderBottomColor: 'rgba(15, 23, 42, 0.06)',
                                    py: 1.5,
                                  },
                                }}
                            >
                              <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                                {day._id}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                                {day.count}건
                              </TableCell>
                              <TableCell
                                  align="right"
                                  sx={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}
                              >
                                {formatCurrency(day.total)}
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={pct}
                                    sx={{
                                      height: 9,
                                      borderRadius: 999,
                                      backgroundColor: '#EEF2F7',
                                      '& .MuiLinearProgress-bar': {
                                        borderRadius: 999,
                                      },
                                    }}
                                />
                              </TableCell>
                            </TableRow>
                        );
                      })
                  ) : (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <EmptyState text="표시할 매출 데이터가 없습니다." />
                        </TableCell>
                      </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          {/* Right: Side Stack */}
          <Box
              sx={{
                display: 'grid',
                gridTemplateRows: { xs: 'auto', lg: '1fr 1fr' },
                gap: 3,
              }}
          >
            <SectionCard
                title="상품별 활성 구독"
                subtitle="현재 활성 상태인 구독 상품 비중입니다."
                minHeight={255}
            >
              <Stack spacing={2}>
                {stats?.productStats?.length ? (
                    // 매 렌더마다 순서 바뀌는 버그 방지 — count 내림차순 → _id 알파벳순 보조정렬
                    [...stats.productStats]
                      .sort((a, b) => (b.count - a.count) || a._id.localeCompare(b._id))
                      .map((ps) => {
                      const total = stats.activeSubscriptions || 1;
                      const pct = ((ps.count / total) * 100).toFixed(1);
                      const color =
                          ps._id === 'all_in_one_subscription' ? '#2563EB' : '#9333EA';

                      return (
                          <Box key={ps._id}>
                            <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: 0.75,
                                  gap: 1,
                                }}
                            >
                              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                {productLabel[ps._id] || ps._id}
                              </Typography>
                              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                                {ps.count}건 · {pct}%
                              </Typography>
                            </Box>

                            <LinearProgress
                                variant="determinate"
                                value={Number(pct)}
                                sx={{
                                  height: 10,
                                  borderRadius: 999,
                                  backgroundColor: '#EEF2F7',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 999,
                                    backgroundColor: color,
                                  },
                                }}
                            />
                          </Box>
                      );
                    })
                ) : (
                    <EmptyState text="구독 상품 데이터가 없습니다." />
                )}
              </Stack>
            </SectionCard>

            <SectionCard
                title="가입 경로"
                subtitle="회원 유입 채널을 빠르게 파악합니다."
                minHeight={255}
            >
              {stats?.providerStats?.length ? (
                  <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                      }}
                  >
                    {/* count 내림차순 정렬 — 순서 변동 방지 */}
                    {[...stats.providerStats]
                      .sort((a, b) => (b.count - a.count) || a._id.localeCompare(b._id))
                      .map((ps) => {
                        const c = providerColor[ps._id] || providerColor.local;
                        return (
                          <Box
                              key={ps._id}
                              sx={{
                                px: 1.5,
                                py: 1.25,
                                borderRadius: '12px',
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: '#FCFCFD',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                          >
                            <Chip
                                label={providerLabel[ps._id] || ps._id}
                                size="small"
                                sx={{
                                  borderRadius: 999,
                                  fontWeight: 600,
                                  fontSize: 11,
                                  backgroundColor: c.bg,
                                  color: c.fg,
                                  border: `1px solid ${c.border}`,
                                }}
                            />
                            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                              {ps.count}명
                            </Typography>
                          </Box>
                        );
                      })}
                  </Box>
              ) : (
                  <EmptyState text="가입 경로 데이터가 없습니다." />
              )}
            </SectionCard>
          </Box>
        </Box>

        {/* Heatmap */}
        <Box sx={{ mt: 3 }}>
          <SectionCard
            title="시간대별 이용 현황"
            subtitle="최근 6개월 기준 요일 × 시간대별 세차 이용 건수입니다. 진할수록 이용이 많습니다."
            minHeight={240}
          >
            {heatmap.length > 0 ? (
              <UsageHeatmap data={heatmap} />
            ) : (
              <EmptyState text="이용 데이터가 없습니다." />
            )}
          </SectionCard>
        </Box>

        {/* Weather Strip */}
        {(() => {
          const selectedStore = stores.find((s) => s._id === selectedStoreId);
          const weatherLat = selectedStore?.lat ?? DEFAULT_LAT;
          const weatherLng = selectedStore?.lng ?? DEFAULT_LNG;
          const locationLabel = selectedStore?.name
            ? `${selectedStore.name} (${selectedStore.address || selectedStore.name})`
            : '서울 (기본)';

          return (
            // 다른 카드(SectionCard)와 동일하게 p:3, mb:2.5 통일
            <Paper variant="outlined" sx={{ ...surfaceSx, p: 3, mb: 3, mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 700, letterSpacing: '-0.02em' }}>
                    주간 날씨
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                    📍 {locationLabel}
                  </Typography>
                </Box>

                {/* master만 지점 탭 표시 */}
                {role === 'master' && stores.length > 0 && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      p: 0.5,
                      borderRadius: 999,
                      backgroundColor: '#F1F5F9',  // 토글 그룹 배경
                      border: '1px solid rgba(0,0,0,0.04)',
                    }}
                  >
                    {[...stores].sort((a, b) => {
                      const aTemp = a.name?.includes('임시') ? 1 : 0;
                      const bTemp = b.name?.includes('임시') ? 1 : 0;
                      return aTemp - bTemp || a.name.localeCompare(b.name, 'ko');
                    }).map((s) => {
                      const isSelected = selectedStoreId === s._id;
                      return (
                        <Box
                          key={s._id}
                          component="button"
                          onClick={() => setSelectedStoreId(s._id)}
                          sx={{
                            border: 'none',
                            cursor: 'pointer',
                            px: 2,                            // 더 넉넉한 좌우
                            py: 0.9,
                            borderRadius: 999,
                            fontSize: 13,
                            fontWeight: 700,
                            fontFamily: 'inherit',
                            letterSpacing: '-0.01em',
                            transition: 'all 0.2s ease',
                            backgroundColor: isSelected ? '#1976d2' : 'transparent',
                            color: isSelected ? '#fff' : '#64748B',
                            boxShadow: isSelected ? '0 2px 6px rgba(25,118,210,0.25)' : 'none',
                            '&:hover': {
                              backgroundColor: isSelected ? '#1565C0' : 'rgba(0,0,0,0.04)',
                              color: isSelected ? '#fff' : '#1E293B',
                            },
                          }}
                        >
                          {s.name}
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {/* 좌표 없는 지점은 안내 표시 (토글 그룹 밖) */}
                {role === 'master' && selectedStore && !selectedStore.lat && (
                  <Typography sx={{ fontSize: 11, color: 'warning.main', alignSelf: 'center', width: '100%' }}>
                    ※ 좌표 미설정 — 서울 기본값 사용
                  </Typography>
                )}
              </Box>
              <WeatherWidget lat={weatherLat} lng={weatherLng} />
            </Paper>
          );
        })()}

        {/* Bottom Area */}
        <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 3,
            }}
        >
          <SectionCard
              title="최근 공지사항"
              subtitle="관리자가 최근 등록한 공지입니다."
              minHeight={320}
          >
            {stats?.recentNotices?.length ? (
                <List disablePadding>
                  {stats.recentNotices.map((notice, i) => (
                      <React.Fragment key={notice._id}>
                        <ListItem disablePadding>
                          <ListItemButton
                              onClick={() => navigate('/notices')}
                              sx={{ px: 0, py: 1.25, borderRadius: '8px' }}
                          >
                            <ListItemText
                                primary={notice.title}
                                secondary={formatDate(notice.createdAt)}
                                slotProps={{
                                  primary: {
                                    fontSize: 14,
                                    fontWeight: 600,
                                  },
                                  secondary: {
                                    fontSize: 12,
                                    color: 'text.secondary',
                                  },
                                }}
                            />
                          </ListItemButton>
                        </ListItem>
                        {i < stats.recentNotices.length - 1 && <Divider />}
                      </React.Fragment>
                  ))}
                </List>
            ) : (
                <EmptyState text="최근 공지사항이 없습니다." />
            )}
          </SectionCard>

          <SectionCard
              title="최근 가입 회원"
              subtitle="가장 최근에 가입한 회원 목록입니다."
              minHeight={320}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>
                      이름
                    </TableCell>
                    {/* 이메일: 좁은 viewport에서 숨김 (이름+경로+날짜만으로도 식별 가능) */}
                    <TableCell sx={{ fontWeight: 700, color: 'text.secondary', display: { xs: 'none', md: 'table-cell' } }}>
                      이메일
                    </TableCell>
                    <TableCell
                        align="center"
                        sx={{ fontWeight: 700, color: 'text.secondary' }}
                    >
                      가입경로
                    </TableCell>
                    <TableCell
                        align="right"
                        sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}
                    >
                      가입일
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {stats?.recentMembers?.length ? (
                      stats.recentMembers.map((m) => (
                          <TableRow
                              key={m._id}
                              hover
                              onClick={() => navigate(`/members/${m._id}`)}
                              sx={{
                                cursor: 'pointer',
                                '& td': {
                                  borderBottomColor: 'rgba(15, 23, 42, 0.06)',
                                  py: 1.5,
                                },
                              }}
                          >
                            <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>
                              {m.memberNickname || '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: 13, color: 'text.secondary', display: { xs: 'none', md: 'table-cell' }, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {m.memberEmail || '-'}
                            </TableCell>
                            <TableCell align="center">
                              {(() => {
                                const c = providerColor[m.provider] || providerColor.local;
                                return (
                                  <Chip
                                    label={providerLabel[m.provider] || m.provider}
                                    size="small"
                                    sx={{
                                      borderRadius: 999,
                                      fontWeight: 600,
                                      fontSize: 11,
                                      backgroundColor: c.bg,
                                      color: c.fg,
                                      border: `1px solid ${c.border}`,
                                    }}
                                  />
                                );
                              })()}
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap' }}
                            >
                              {formatDate(m.createdAt)}
                            </TableCell>
                          </TableRow>
                      ))
                  ) : (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <EmptyState text="최근 가입 회원이 없습니다." />
                        </TableCell>
                      </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </Box>
        </>
        )}

      </Box>
  );
};

export default DashboardPage;
